import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

const slides = [
{
    id: 'dubai',
    image: '/hero/Dubai.jpg',
    imageMobile: '/hero/Dubai.jpg',
    title: 'Dubai',
    subtitle: 'Luxury, light and limitless horizons.',
},
  {
    id: 'goa',
    image: '/hero/Goa.jpg',
    imageMobile: '/hero/Goa.jpg',
    title: 'Goa',
    subtitle: "Unhurried days on India's sunlit coast.",
  },
  {
    id: 'kedar-badri',
    image: '/hero/KedarBadri.jpg',
    imageMobile: '/hero/KedarBadri.jpg',
    title: 'Kedar Badri',
    subtitle: 'A sacred journey through the Himalayas.',
  },
  {
    id: 'kerala',
    image: '/hero/Kerala.jpg',
    imageMobile: '/hero/Kerala.jpg',
    title: 'Kerala',
    subtitle: "Slow down in nature's own country.",
  },
  {
    id: 'maldives',
    image: '/hero/Maldives.jpg',
    imageMobile: '/hero/Maldives.jpg',
    title: 'Maldives',
    subtitle: 'Your private escape in shades of blue.',
  },
  {
    id: 'malaysia',
    image: '/hero/Malaysia.jpg',
    imageMobile: '/hero/Malaysia.jpg',
    title: 'Malaysia',
    subtitle: 'A vivid blend of culture, city and coast.',
  },
  {
    id: 'srilanka',
    image: '/hero/SriLanka.jpeg',
    imageMobile: '/hero/SriLanka.jpeg',
    title: 'Sri Lanka',
    subtitle: 'Wild, welcoming and wonderfully close.',
  },
];

const HeroSection = ({ searchQuery, setSearchQuery, onSearch }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);

  const totalSlides = slides.length;
  const currentSlide = slides[activeSlide];

  const goToSlide = useCallback(
    (index) => setActiveSlide((index + totalSlides) % totalSlides),
    [totalSlides]
  );

  useEffect(() => {
    if (isPaused) return undefined;
    const interval = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % totalSlides),
      6500
    );
    return () => window.clearInterval(interval);
  }, [isPaused, totalSlides]);

  // Preload the next slide's image so transitions are instant without
  // competing with the visible image for the network connection.
  useEffect(() => {
    const next = slides[(activeSlide + 1) % totalSlides];
    const img = new Image();
    img.src = next.image;
  }, [activeSlide, totalSlides]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSearch?.(event);
  };

  const handleTouchEnd = (event) => {
    const distance = touchStartX.current - event.changedTouches[0].clientX;
    if (Math.abs(distance) > 48) goToSlide(activeSlide + (distance > 0 ? 1 : -1));
  };

  return (
    <Box
      component="section"
      aria-label="Featured destinations"
      sx={{
        bgcolor: '#ffffff', 
        px: { xs: 1.5, sm: 3, lg: 4 },
        py: { xs: 1.5, sm: 3, lg: 0 },
      }}
    >
      <Box
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0].clientX;
        }}
        onTouchEnd={handleTouchEnd}
        sx={{
          isolation: 'isolate',
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 600, sm: 650, md: 700 },
          height: { md: 'calc(100vh - 64px)' },
          maxHeight: 820,
          borderRadius: { xs: 3, sm: 4, lg: 5 },
          bgcolor: '#142220',
          boxShadow: '0 24px 70px rgba(22, 35, 32, 0.18)',
        }}
      >
        {slides.map((slide, index) => {
          const isActive = index === activeSlide;
          const hasFailed = failedImages[index];

          return (
            <Box
              key={slide.id}
              aria-hidden={!isActive}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: isActive ? 1 : 0,
                pointerEvents: isActive ? 'auto' : 'none',
                transition: 'opacity 900ms ease-in-out',
              }}
            >
              {isActive && !hasFailed && (
                <Box
                  component="img"
                  src={slide.imageMobile}
                  srcSet={`${slide.imageMobile} 800w, ${slide.image} 1920w`}
                  sizes="100vw"
                  alt={isActive ? `${slide.title} travel destination` : ''}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  onLoad={() => setLoadedImages((value) => ({ ...value, [index]: true }))}
                  onError={() => setFailedImages((value) => ({ ...value, [index]: true }))}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: loadedImages[index] ? 1 : 0,
                    transform: isActive ? 'scale(1.055)' : 'scale(1.015)',
                    transition: 'opacity 500ms ease, transform 7s ease-out',
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(90deg, rgba(11, 25, 22, 0.79) 0%, rgba(11, 25, 22, 0.48) 42%, rgba(11, 25, 22, 0.08) 75%), linear-gradient(0deg, rgba(6, 18, 16, 0.64) 0%, transparent 42%)',
                }}
              />
            </Box>
          );
        })}

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 'inherit',
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 7, md: 9 },
          }}
        >
          <Box sx={{ maxWidth: 690, mb: { xs: 4, md: 5 }, textAlign: 'left' }}>
            <Typography
              sx={{
                display: 'block',
                color: '#f6cf6c',
                fontSize: { xs: '0.68rem', md: '0.75rem' },
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                m: 0,
              }}
            >
              Curated journeys
            </Typography>
            <Typography
              component="h1"
              sx={{
                mt: 1.5,
                color: '#fff',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: { xs: '3.35rem', sm: '4.75rem', md: '6rem' },
                fontWeight: 500,
                letterSpacing: '-0.055em',
                lineHeight: 0.94,
                textShadow: '0 5px 26px rgba(0,0,0,0.23)',
              }}
            >
              {currentSlide.title}
            </Typography>
            <Typography
              sx={{
                mt: 2.5,
                maxWidth: 450,
                color: 'rgba(255,255,255,0.88)',
                fontSize: { xs: '1rem', md: '1.16rem' },
                fontWeight: 400,
                letterSpacing: '0.01em',
                lineHeight: 1.6,
              }}
            >
              {currentSlide.subtitle}
            </Typography>
          </Box>

          <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: { xs: '100%', sm: 680, md: 750 },
              mx: 'auto',
              p: { xs: 1, sm: 1.25 },
              border: '1px solid rgba(255,255,255,0.42)',
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.94)',
              boxShadow: '0 18px 46px rgba(0,0,0,0.25)',
              backdropFilter: 'blur(18px)',
            }}
          >
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Where would you like to go?"
              variant="standard"
              inputProps={{ 'aria-label': 'Search for a destination' }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnOutlinedIcon sx={{ color: '#52706a', fontSize: 24 }} />
                  </InputAdornment>
                ),
                sx: {
                  px: { xs: 1.5, sm: 2 },
                  color: '#18312c',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 500,
                  '& input::placeholder': { color: '#70827e', opacity: 1 },
                },
              }}
            />
            <IconButton
              type="submit"
              aria-label="Search destinations"
              sx={{
                flex: '0 0 auto',
                width: { xs: 50, sm: 58 },
                height: { xs: 50, sm: 58 },
                borderRadius: 2.25,
                color: '#fff',
                bgcolor: '#173e36',
                boxShadow: '0 7px 18px rgba(23,62,54,0.27)',
                transition: 'transform 180ms ease, background-color 180ms ease',
                '&:hover': { bgcolor: '#0e2d27', transform: 'translateY(-1px)' },
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Paper>
        </Container>

        <Box
          sx={{
            position: 'absolute',
            zIndex: 2,
            left: { xs: 26, sm: 42, lg: 56 },
            bottom: { xs: 29, md: 40 },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {slides.map((slide, index) => (
            <Box
              key={slide.id}
              component="button"
              onClick={() => goToSlide(index)}
              aria-label={`Show ${slide.title}`}
              aria-current={index === activeSlide ? 'true' : undefined}
              sx={{
                appearance: 'none',
                border: 0,
                p: 0,
                width: index === activeSlide ? 30 : 7,
                height: 7,
                borderRadius: 10,
                bgcolor: index === activeSlide ? '#f6cf6c' : 'rgba(255,255,255,0.55)',
                cursor: 'pointer',
                transition: 'width 250ms ease, background-color 250ms ease',
                '&:hover': { bgcolor: '#fff' },
                '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 4 },
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            zIndex: 2,
            right: { xs: 20, sm: 28, lg: 36 },
            bottom: { xs: 20, md: 31 },
            display: { xs: 'none', sm: 'flex' },
            gap: 1,
          }}
        >
          <IconButton
            onClick={() => goToSlide(activeSlide - 1)}
            aria-label="Previous destination"
            sx={navButtonSx}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => goToSlide(activeSlide + 1)}
            aria-label="Next destination"
            sx={navButtonSx}
          >
            <ArrowForwardRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const navButtonSx = {
  width: 43,
  height: 43,
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.45)',
  bgcolor: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(10px)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
};

export default HeroSection;